import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import Gallery from "react-photo-gallery";
// import Carousel, { Modal, ModalGateway } from "react-images";
import { motion, useAnimate } from "framer-motion";
import { urlFor, client } from "../../client";
import { getImageDimensions } from "@sanity/asset-utils";

import "./FilmGallery.scss";

const FilmGallery = () => {
  const HEADER_OFFSET = 145;
  const [showBackToTop, setShowBackToTop] = useState(false);

  // Sanity data
  const [photos, setPhotos] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Lightbox state
  // const [currentImage, setCurrentImage] = useState(0);
  // const [viewerIsOpen, setViewerIsOpen] = useState(false);
  // const [activeGallery, setActiveGallery] = useState([]);

  // recommended instead of lightbox:
  const [activeGallery, setActiveGallery] = useState([]);
  const [activeIndex, setActiveIndex] = useState(null);


  // Scroll refs
  const sectionRefs = useRef({});
  const topRef = useRef(null);

  // Fetch photos
  useEffect(() => {
    const query = '*[_type == "filmPhotos"]';
    client.fetch(query).then((data) => {
      setPhotos(data);
      setIsLoading(false);
    });
  }, []);

  /**
   * Group photos by trip (NO sorting of photos)
   */
  const photosByTrip = useMemo(() => {
    const map = {};

    photos.forEach((photo) => {
      if (!photo.trip) return;

      if (!map[photo.trip]) {
        map[photo.trip] = [];
      }

      map[photo.trip].push(photo);
    });

    return map;
  }, [photos]);

  /**
   * Sort trips by most recent photo date
   */
  const sortedTrips = useMemo(() => {
    return Object.entries(photosByTrip)
      .map(([trip, tripPhotos]) => {
        const mostRecentDate = tripPhotos.reduce((latest, photo) => {
          const d = Date.parse(photo.date);
          return isNaN(d) ? latest : Math.max(latest, d);
        }, 0);

        return {
          trip,
          photos: tripPhotos,
          mostRecentDate,
        };
      })
      .sort((a, b) => b.mostRecentDate - a.mostRecentDate);
  }, [photosByTrip]);

  /**
   * Format photos for react-photo-gallery
   */
  const formatPhotos = (photoArray) =>
    photoArray.map((photo) => {
      const imageUrl = urlFor(photo.imgUrl).toString();

      return {
        src: imageUrl,
        width: getImageDimensions({ url: imageUrl }).aspectRatio,
        height: 1,
        name: photo.name,
        description: `Captured ${photo.date} in ${photo.location} (${photo.cameraType}, ${photo.filmType})`,
      };
    });

  // Lightbox handlers
  // const openLightbox = useCallback((galleryPhotos, index) => {
  //   setActiveGallery(galleryPhotos);
  //   setCurrentImage(index);
  //   setViewerIsOpen(true);
  // }, []);

  // const closeLightbox = () => {
  //   setCurrentImage(0);
  //   setViewerIsOpen(false);
  //   setActiveGallery([]);
  // };

  const closeViewer = () => {
    setActiveIndex(null);
    setActiveGallery([]);
  };
  
  const showPrev = () => {
    setActiveIndex((i) =>
      i > 0 ? i - 1 : activeGallery.length - 1
    );
  };
  
  const showNext = () => {
    setActiveIndex((i) =>
      i < activeGallery.length - 1 ? i + 1 : 0
    );
  };

  useEffect(() => {
    if (activeIndex === null) return;
  
    const onKeyDown = (e) => {
      if (e.key === "Escape") closeViewer();
      if (e.key === "ArrowLeft") showPrev();
      if (e.key === "ArrowRight") showNext();
    };
  
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [activeIndex, activeGallery]);

  // Loader animation
  const text = "PHOTOS LOADING...";
  const characters = text.split("");
  const radius = 80;
  const fontSize = "22px";
  const letterSpacing = 18.5;
  const [scope, animate] = useAnimate();

  useEffect(() => {
    const animateLoader = async () => {
      const sequence = [];

      characters.forEach((_, i) => {
        sequence.push([
          `.letter-${i}`,
          { opacity: 0 },
          { duration: 0.3, at: i === 0 ? "+0.8" : "-0.28" },
        ]);
      });

      characters.forEach((_, i) => {
        sequence.push([
          `.letter-${i}`,
          { opacity: 1 },
          { duration: 0.3, at: i === 0 ? "+0.8" : "-0.28" },
        ]);
      });

      animate(sequence, { ease: "linear", repeat: Infinity });
      animate(scope.current, { rotate: 360 }, { duration: 4, ease: "linear", repeat: Infinity });
    };

    animateLoader();
  }, []);

  const scrollToSection = (trip) => {
    const el = sectionRefs.current[trip];
    if (!el) return;

    const y = el.getBoundingClientRect().top + window.pageYOffset - HEADER_OFFSET;

    window.scrollTo({ top: y, behavior: "smooth" });
  };

  useEffect(() => {
    const onScroll = () => {
      setShowBackToTop(window.scrollY > 300);
    };

    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="app__filmgallery app__flex">
      {/* Header */}
      <motion.div
        whileInView={{ opacity: [0, 1] }}
        transition={{ duration: 0.5 }}
        className="app__header-info"
      >
        <div className="app__filmgallery-header">
          <div className="head-text">Film Photos</div>
        </div>
      </motion.div>

      {/* Top navigation */}
      <div ref={topRef} className="app__photos-filters">
        <p className="bold-text">Pick a trip, any trip!</p>
        <div className="btnContainer">
          {sortedTrips.map(({ trip }) => (
            <button
              key={trip}
              className="btn"
              onClick={() => scrollToSection(trip)}
            >
              {trip}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="app__photos">
        {isLoading ? (
          <motion.div ref={scope} className="loading_circle" style={{ width: radius * 2 }}>
            <p aria-hidden="true" className="text">
              {characters.map((ch, i) => (
                <motion.span
                  key={i}
                  className={`letter letter-${i}`}
                  style={{
                    transformOrigin: `0 ${radius}px`,
                    transform: `rotate(${i * letterSpacing}deg)`,
                    fontSize,
                  }}
                >
                  {ch}
                </motion.span>
              ))}
            </p>
          </motion.div>
        ) : (
          <>
            {sortedTrips.map(({ trip, photos, mostRecentDate }) => {
              const galleryPhotos = formatPhotos(photos);
              const formattedMonth = mostRecentDate
                ? new Date(mostRecentDate).toLocaleDateString("en-US", {
                    month: "long",
                    year: "numeric",
                  })
                : "";

              return (
                <section
                  key={trip}
                  ref={(el) => (sectionRefs.current[trip] = el)}
                  className="app__photos-section"
                >
                  <h2 className="head-text section-title">
                    <span>{trip}</span>
                  </h2>
                  <p className="p-text">{formattedMonth}</p>

                  <Gallery
                    photos={galleryPhotos}
                    targetRowHeight={400}
                    // onClick={(e, { index }) =>
                    //   openLightbox(galleryPhotos, index)
                    // }
                    onClick={(e, { index }) => {
                      setActiveGallery(galleryPhotos);
                      setActiveIndex(index);
                    }}
                  />
                </section>
              );
            })}
          </>
        )}
      </div>

      {/* Lightbox */}
      {/* <ModalGateway>
        {viewerIsOpen && (
          <Modal onClose={closeLightbox}>
            <Carousel
              currentIndex={currentImage}
              views={activeGallery.map((img) => ({
                src: img.src,
                caption: `<h2>${img.name}</h2>${img.description}`,
              }))}
            />
          </Modal>
        )}
      </ModalGateway> */}
      {activeIndex !== null && (
        <div className="photo-overlay" onClick={closeViewer}>
          <button
            className="nav-arrow prev"
            onClick={(e) => {
              e.stopPropagation();
              showPrev();
            }}
            aria-label="Previous photo"
          />

          <img
            src={activeGallery[activeIndex].src}
            alt={activeGallery[activeIndex].name}
            onClick={(e) => e.stopPropagation()}
          />

          <button
            className="nav-arrow next"
            onClick={(e) => {
              e.stopPropagation();
              showNext();
            }}
            aria-label="Next photo"
          />

          <div className="photo-meta">
            <h2>{activeGallery[activeIndex].name}</h2>
            <p>{activeGallery[activeIndex].description}</p>
          </div>
        </div>
      )}


      {/* Back to top */}
      <button
        className={`back-to-top ${showBackToTop ? "visible" : ""}`}
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        aria-label="Back to top"
      >
        <p>Back to Top?</p>
      </button>
    </div>
  );
};

export default FilmGallery;
